import { getUserByEmail, getUserByEmailExists } from '@adornis/buchungsheld-db/api/users.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { registerMwAdminUser } from '../../../api/admin.js';
import { isValidSingleEmail } from '../../../shared/email.js';

export const AdminCreateAdmin = stateful(() => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [existingUser, setExistingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const emailValid = isValidSingleEmail(email);
  const allValid = emailValid && firstName.trim() && lastName.trim() && password.trim();

  const checkEmail = async () => {
    setError(null);
    const mail = email.trim();
    if (!mail) {
      setExistingUser(null);
      return;
    }
    setChecking(true);
    try {
      const exists = await getUserByEmailExists(mail)();
      if (exists) {
        const user = await getUserByEmail(mail)({ _id: 1, email: 1, firstName: 1, lastName: 1 });
        setExistingUser(user ?? null);
      } else {
        setExistingUser(null);
      }
    } catch (e) {
      console.error(e);
      setError(m.admin_create_email_error());
    } finally {
      setChecking(false);
    }
  };

  const onSubmit = async () => {
    setError(null);
    setSuccess(null);
    if (!allValid) return;
    // re-check email existence before creating (avoid relying on possibly stale state)
    const mail = email.trim();
    try {
      const existsNow = await getUserByEmailExists(mail)();
      if (existsNow) {
        setError(m.admin_create_email_exists());
        await checkEmail();
        return;
      }
    } catch {
      // ignore — the registerMwAdminUser call below will surface a hard error if it matters
    }
    setLoading(true);
    try {
      await registerMwAdminUser(email.trim(), password.trim(), firstName.trim(), lastName.trim())();

      setSuccess(m.admin_create_success());
      setEmail('');
      setFirstName('');
      setLastName('');
      setPassword('');
      setExistingUser(null);
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? m.admin_create_email_error());
    } finally {
      setLoading(false);
    }
  };

  return html`
    <div class="p-8">
      <div class="flex flex-col items-center mb-6">
        <h1 class="text-3xl font-bold text-base-content">${m.admin_create_title()}</h1>
      </div>

      <div class="card bg-base-100 shadow">
        <div class="card-body gap-4 max-w-xl">
          ${error ? html`<div class="alert alert-error">${error}</div>` : null}
          ${success ? html`<div class="alert alert-success">${success}</div>` : null}

          <label class="form-control w-full">
            <div class="label"><span class="label-text">${m.email()}</span></div>
            <input
              class="input input-bordered w-full"
              type="email"
              placeholder="user@example.com"
              .value=${email}
              @input=${(e: InputEvent) => setEmail((e.target as HTMLInputElement).value)}
              @blur=${() => checkEmail()}
            />
            ${checking
              ? html`<div class="label"><span class="label-text-alt">${m.admin_create_checking_email()}</span></div>`
              : existingUser
                ? html`<div class="mt-2 card bg-base-200">
                    <div class="card-body p-3">
                      <div class="text-sm">${m.admin_create_user_exists()}</div>
                      <div class="font-semibold">${existingUser.firstName} ${existingUser.lastName}</div>
                      <div class="badge badge-outline mt-1">${existingUser.email}</div>
                    </div>
                  </div>`
                : null}
          </label>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label class="form-control w-full">
              <div class="label"><span class="label-text">${m.admin_create_first_name()}</span></div>
              <input
                class="input input-bordered w-full"
                type="text"
                placeholder="Max"
                .value=${firstName}
                @input=${(e: InputEvent) => setFirstName((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="form-control w-full">
              <div class="label"><span class="label-text">${m.admin_create_last_name()}</span></div>
              <input
                class="input input-bordered w-full"
                type="text"
                placeholder="Mustermann"
                .value=${lastName}
                @input=${(e: InputEvent) => setLastName((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>

          <label class="form-control w-full">
            <div class="label"><span class="label-text">${m.admin_create_password()}</span></div>
            <input
              class="input input-bordered w-full"
              type="password"
              placeholder="••••••••"
              .value=${password}
              @input=${(e: InputEvent) => setPassword((e.target as HTMLInputElement).value)}
            />
            <div class="label"><span class="label-text-alt opacity-70">${m.admin_create_password_hint()}</span></div>
          </label>

          <div class="card-actions nono justify-end mt-2">
            <button class="btn btn-primary" ?disabled=${loading || !allValid} @click=${onSubmit}>
              ${loading
                ? html`<span class="loading loading-spinner"></span>`
                : html`<span class="icon-[material-symbols--person-add]"></span>`}
              ${m.admin_create_submit()}
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
});
